<script setup>
  import {computed, onBeforeUnmount, ref, watch} from "vue";

  import AiResultCard from "@components/_DevOnly/AiResultCard.vue";
  import AiStatusBadge from "@components/_DevOnly/AiStatusBadge.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import ComponentTextarea from "@components/Textarea/Textarea.vue";
  import {aiProviders, getModelSettings} from "@enums/aiEnums";
  import {
    MedButton,
    MedCard,
    MedCardContent,
    MedCardHeader,
    MedCheckbox,
    MedFormField,
    MedLabel,
  } from "@pharma4u/medicheck-components";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";

  defineOptions({
    name: "PagesDevAiDebugger",
  });

  const props = defineProps({
    features: {
      type: Array,
      default: () => [],
    },
    targets: {
      type: Array,
      default: () => [],
    },
  });

  // Constants
  const urlParameters = new URLSearchParams(globalThis.location.search);
  const providers = Object.keys(aiProviders).map((p) => ({key: p, label: p}));

  // Refs: Context
  const id = ref(urlParameters.get("id") || "");
  const feature = ref(urlParameters.get("feature") || "");
  const target = ref(urlParameters.get("target") || "");

  // Refs: Form
  const form = ref(getDefaultForm());
  const placeholders = ref([]);
  const technicalTermsJson = ref("{}");
  const schemaJson = ref("{}");

  // Refs: Loading & Status
  const isLoading = ref(false);
  const resultData = ref(null);
  const errorMessage = ref(null);
  const runId = ref(null);
  const jobStatus = ref(null);
  const apiDurationMs = ref(null);

  // Refs: Timer
  const timerStart = ref(null);
  const timerEnd = ref(null);
  const elapsedSeconds = ref(0);

  // Refs: History
  const history = ref([]);
  const historyLoading = ref(false);

  // Interval Variables
  let pollInterval = null;
  let elapsedInterval = null;

  // Computed: Options
  const featureOptions = computed(() => props.features.map((f) => ({key: f, label: f})));
  const targetOptions = computed(() => props.targets.map((t) => ({key: t, label: t})));

  // Computed: Provider & Model
  const filteredModels = computed(() => {
    const models = aiProviders[form.value.provider]?.models ?? {};
    return Object.keys(models).map((m) => ({key: m, label: m}));
  });
  const selectedModel = computed(() => form.value.model || "");
  const selectedProvider = computed(() => form.value.provider || "");
  const modelSettings = computed(() => getModelSettings(selectedProvider.value, selectedModel.value));
  const supportsTemperature = computed(() => modelSettings.value.supportsTemperature);
  const supportsThinking = computed(() => modelSettings.value.supportsThinking);
  const supportsThinkingBudget = computed(() => modelSettings.value.supportsThinkingBudget);
  const supportsTopP = computed(() => modelSettings.value.supportsTopP);
  const thinkingLevels = computed(() => modelSettings.value.thinkingLevels);

  // Computed: Form State
  const canGenerate = computed(() => {
    return !isLoading.value && form.value.provider && form.value.model && form.value.prompt;
  });

  // Computed: Display
  const durationDisplay = computed(() => {
    if (apiDurationMs.value != null) {
      return (apiDurationMs.value / 1000).toFixed(1) + "s";
    }
    if (timerStart.value && !timerEnd.value) {
      return elapsedSeconds.value + "s ...";
    }
    if (timerStart.value && timerEnd.value) {
      return ((timerEnd.value - timerStart.value) / 1000).toFixed(1) + "s";
    }
    return "-";
  });

  // Watchers
  watch(
    () => form.value.provider,
    (newValue) => {
      const providerModels = aiProviders[newValue]?.models ?? {};
      if (!Object.keys(providerModels).includes(form.value.model)) {
        form.value.model = "";
      }

      if (!supportsThinking.value) {
        form.value.thinking_level = null;
      }

      if (!supportsThinkingBudget.value) {
        form.value.thinking_budget = null;
      }

      if (!supportsTopP.value) {
        form.value.top_p = null;
      }

      if (!supportsTemperature.value) {
        form.value.temperature = null;
      }

      form.value.include_thoughts = supportsThinking.value;
    },
  );

  watch(
    form,
    () => {
      if (!thinkingLevels.value.includes(form.value.thinking_level)) {
        form.value.thinking_level = null;
      }
    },
    {deep: true},
  );

  // Functions: Form Defaults & Reset
  function getDefaultForm() {
    return {
      provider: "",
      model: "",
      system_prompt: "",
      prompt: "",
      include_thoughts: false,
      thinking_level: null,
      thinking_budget: null,
      temperature: null,
      top_p: null,
      replace_technical_terms: false,
      enable_schema: false,
      max_steps: null,
      max_tokens: null,
    };
  }

  function resetForm() {
    form.value = getDefaultForm();
    placeholders.value = [];
    technicalTermsJson.value = "{}";
    schemaJson.value = "{}";
  }

  function resetResult() {
    resultData.value = null;
    errorMessage.value = null;
    runId.value = null;
    jobStatus.value = null;
    apiDurationMs.value = null;
  }

  // Functions: Placeholders
  function addPlaceholder() {
    const name = globalThis.prompt("Placeholder Name (without %):");
    if (!name) return;
    placeholders.value.push({key: name.trim(), value: "", isJson: false, isCustom: true});
  }

  function insertPlaceholderIntoPrompt(key) {
    form.value.prompt = (form.value.prompt || "") + "%" + key + "%";
  }

  function removePlaceholder(index) {
    placeholders.value.splice(index, 1);
  }

  // Functions: Data Loading
  function loadInitialData() {
    if (!id.value || !feature.value) return;

    const params = {
      id: id.value,
      feature: feature.value,
      target: target.value,
    };

    resetForm();
    resetResult();

    useAuthHttp().get(route("ai-debugger.get-initial-data", params), {
      onSuccess: (response) => {
        console.log("initial-data", response);

        if (response.placeholders) {
          placeholders.value = Object.entries(response.placeholders).map(([key, value]) => {
            let isJson = true;

            try {
              value = JSON.stringify(JSON.parse(value), null, 2);
            } catch {
              isJson = false;
            }

            return {key, value, isJson, isCustom: false};
          });
        }

        if (response.technical_terms) {
          technicalTermsJson.value = JSON.stringify(response.technical_terms, null, 2);
        }

        if (response.schema) {
          schemaJson.value = JSON.stringify(response.schema, null, 2);
        }

        for (const key of Object.keys(response)) {
          if (key in form.value) {
            form.value[key] = response[key];
          }
        }
      },
    });
  }

  // Functions: Validation
  function validateJsonPlaceholders() {
    placeholders.value.forEach((item) => {
      if (!item.isJson) return;

      try {
        JSON.parse(item.value);
      } catch {
        alert(
          'Note on placeholder "' +
            item.key +
            '":\n\n' +
            "The content is not valid JSON and could not be compressed.\n\n" +
            "The prompt will still be generated.\n" +
            "However, it will be larger, slower, and consume more tokens.\n\n" +
            "Tip: Valid JSON saves tokens and improves performance.",
        );
      }
    });
  }

  // Functions: Queue & Generation
  function startQueueGeneration() {
    resetResult();
    isLoading.value = true;
    timerStart.value = Date.now();
    timerEnd.value = null;
    elapsedSeconds.value = 0;

    validateJsonPlaceholders();

    let parsedTerms = [];
    if (form.value.replace_technical_terms) {
      try {
        const parsed = JSON.parse(technicalTermsJson.value);
        parsedTerms = Object.entries(parsed).map(([term, replacement]) => ({term, replacement}));
      } catch {
        isLoading.value = false;
        timerEnd.value = Date.now();
        errorMessage.value = "Technical Terms JSON is invalid. Please fix the JSON syntax and try again.";
        return;
      }
    }

    const payload = {
      ...form.value,
      placeholders: placeholders.value,
      technical_terms: parsedTerms,
      schema: form.value.enable_schema ? JSON.parse(schemaJson.value) : null,
    };

    const params = {
      id: id.value,
      feature: feature.value,
      target: target.value,
    };

    useAuthHttp(payload)
      .post(route("ai-debugger.queue-start", params))
      .then((response) => {
        runId.value = response.runId;
        jobStatus.value = "pending";
        startPolling();
        startElapsedTimer();
      })
      .catch((error) => {
        isLoading.value = false;
        timerEnd.value = Date.now();
        errorMessage.value = "Queue start failed: " + JSON.stringify(error.message ?? error);
      });
  }

  // Functions: Polling
  function startPolling() {
    stopPolling();
    pollInterval = setInterval(() => {
      if (!runId.value) return;

      useAuthHttp()
        .get(route("ai-debugger.queue-status", {runId: runId.value}))
        .then((response) => {
          jobStatus.value = response.status;

          if (["completed", "failed", "timeout"].includes(response.status)) {
            apiDurationMs.value = response.duration_ms ?? null;
            timerEnd.value = Date.now();
            isLoading.value = false;
            stopPolling();
            stopElapsedTimer();

            if (response.status === "completed") {
              resultData.value = response.result;
            } else {
              errorMessage.value = response.error || (response.status === "timeout" ? "Job timed out." : "Job failed.");
            }

            addToHistory();
          }
        });
    }, 500);
  }

  function stopPolling() {
    if (pollInterval) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
  }

  // Functions: Timer
  function startElapsedTimer() {
    stopElapsedTimer();
    elapsedInterval = setInterval(() => {
      elapsedSeconds.value = ((Date.now() - timerStart.value) / 1000).toFixed(1);
    }, 100);
  }

  function stopElapsedTimer() {
    if (elapsedInterval) {
      clearInterval(elapsedInterval);
      elapsedInterval = null;
    }
  }

  // Functions: Abort
  function abort() {
    stopPolling();
    stopElapsedTimer();
    isLoading.value = false;
    timerEnd.value = Date.now();
    jobStatus.value = "aborted";
  }

  // Functions: History
  function fetchHistory() {
    historyLoading.value = true;
    useAuthHttp()
      .get(route("ai-debugger.history"))
      .then((response) => {
        history.value = response;
      })
      .catch((error) => {
        console.warn("Failed to load history:", error);
      })
      .finally(() => {
        historyLoading.value = false;
      });
  }

  function addToHistory() {
    fetchHistory();
  }

  function restoreFromHistory(entry) {
    historyLoading.value = true;
    useAuthHttp()
      .get(route("ai-debugger.history-detail", {runId: entry.id}))
      .then((data) => {
        id.value = data.contextId ?? "";
        feature.value = data.feature ?? "";
        target.value = data.target ?? "";

        const config = data.config ?? {};
        form.value = {
          ...getDefaultForm(),
          provider: data.provider ?? "",
          model: data.model ?? "",
          system_prompt: data.systemPrompt ?? "",
          prompt: data.userPrompt ?? "",
          include_thoughts: config.include_thoughts ?? false,
          thinking_level: config.thinking_level ?? null,
          thinking_budget: config.thinking_budget ?? null,
          temperature: config.temperature ?? null,
          top_p: config.top_p ?? null,
          replace_technical_terms: config.replace_technical_terms ?? false,
          enable_schema: config.enable_schema ?? false,
          max_steps: config.max_steps ?? null,
          max_tokens: config.max_tokens ?? null,
        };

        placeholders.value = (config.placeholders || []).map((p) => ({...p}));

        if (config.technical_terms && config.technical_terms.length > 0) {
          const termsObject = {};
          for (const t of config.technical_terms) {
            termsObject[t.term] = t.replacement;
          }
          technicalTermsJson.value = JSON.stringify(termsObject, null, 2);
        } else {
          technicalTermsJson.value = "{}";
        }

        schemaJson.value = config.schema ? JSON.stringify(config.schema, null, 2) : "{}";

        resultData.value = data.result ?? null;
        errorMessage.value = data.error ?? null;
        jobStatus.value = data.status ?? null;
        runId.value = data.id;
        apiDurationMs.value = data.durationMs ?? null;

        timerStart.value = data.timestamp;
        timerEnd.value = data.timestamp + (data.durationMs || 0);
        isLoading.value = false;
      })
      .catch((error) => {
        console.warn("Failed to load history detail:", error);
      })
      .finally(() => {
        historyLoading.value = false;
      });
  }

  // Functions: Formatting
  function formatHistoryTimestamp(ts) {
    return new Date(ts).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  function formatDuration(ms) {
    if (!ms) return "-";
    return (ms / 1000).toFixed(1) + "s";
  }

  // Lifecycle Hooks
  onBeforeUnmount(() => {
    stopPolling();
    stopElapsedTimer();
  });

  // Initial Calls
  loadInitialData();
  fetchHistory();
</script>

<template>
  <div class="space-y-6">
    <h1 class="text-2xl font-bold">AI Debugger</h1>

    <div class="flex items-start gap-6">
      <!-- History Card -->
      <MedCard
        as="aside"
        class="sticky top-20 flex w-72 shrink-0 flex-col overflow-hidden"
        :style="{maxHeight: 'calc(100vh - 2rem)'}"
      >
        <MedCardHeader class="mb-2 flex items-center justify-between">
          <span>
            History
            <span class="ml-1 text-xs font-normal">({{ history.length }})</span>
          </span>
          <MedButton variant="secondary" size="sm" :disabled="historyLoading" @click="fetchHistory">
            <ComponentIcon class="text-sm">refresh</ComponentIcon>
          </MedButton>
        </MedCardHeader>

        <MedCardContent v-if="historyLoading && history.length === 0" class="py-12 text-center text-sm">
          Loading history...
        </MedCardContent>

        <MedCardContent v-else-if="history.length === 0" class="py-12 text-center text-sm">
          No entries yet.
          <br />
          Runs will appear here automatically.
        </MedCardContent>

        <MedCardContent v-else class="flex-1 divide-y divide-gray-100 overflow-y-auto p-0!">
          <MedButton
            v-for="entry in history"
            :key="entry.id"
            variant="ghost"
            full-width
            class="group h-auto! items-start text-left"
            @click="restoreFromHistory(entry)"
          >
            <span class="min-w-0 flex-1">
              <span class="flex items-center gap-1.5 text-xs">
                <AiStatusBadge :status="entry.status" icon-only class="shrink-0" />
                <span class="truncate font-medium">{{ entry.model }}</span>
                <span class="ml-auto shrink-0 text-[10px]">{{ formatDuration(entry.durationMs) }}</span>
              </span>
              <span class="mt-0.5 block truncate text-[11px]">
                {{ formatHistoryTimestamp(entry.timestamp) }} · {{ entry.feature }}
                <template v-if="entry.target">/{{ entry.target }}</template>
              </span>
            </span>
          </MedButton>
        </MedCardContent>
      </MedCard>

      <!-- Main Content -->
      <div class="min-w-0 flex-1 space-y-6">
        <!-- Initial Data Params -->
        <MedCard>
          <MedCardHeader>Context</MedCardHeader>
          <MedCardContent>
            <div class="flex gap-4">
              <ComponentInput v-model="id" label="ID" :hide-clear="true" />
              <ComponentSelect
                v-model="feature"
                label="Feature"
                :options="featureOptions"
                key-name="label"
                key-value="key"
                :nullable="true"
                placeholder-text="Select feature..."
              />
              <ComponentSelect
                v-model="target"
                label="Target (optional)"
                :options="targetOptions"
                key-name="label"
                key-value="key"
                :nullable="true"
                placeholder-text="Select target..."
              />
              <div class="flex items-end">
                <MedButton variant="secondary" :disabled="!id || !feature" @click="loadInitialData">Load</MedButton>
              </div>
            </div>
          </MedCardContent>
        </MedCard>

        <!-- Form -->
        <MedCard>
          <MedCardHeader>Configuration</MedCardHeader>
          <MedCardContent class="space-y-4">
            <div class="flex gap-4">
              <ComponentSelect
                v-model="form.provider"
                label="Provider"
                :options="providers"
                key-name="label"
                key-value="key"
              />
              <ComponentSelect
                v-model="form.model"
                label="Model"
                :options="filteredModels"
                key-name="label"
                key-value="key"
                :disabled="!form.provider"
              />
            </div>

            <ComponentTextarea v-model="form.system_prompt" label="System-Prompt" />
            <ComponentTextarea v-model="form.prompt" label="Prompt" />
            <div v-if="placeholders.length > 0" class="flex flex-wrap gap-2">
              <MedButton
                v-for="placeholder in placeholders"
                :key="placeholder.key"
                variant="outline"
                size="sm"
                @click="insertPlaceholderIntoPrompt(placeholder.key)"
              >
                <ComponentIcon class="mr-1 text-sm">add</ComponentIcon>
                Add %{{ placeholder.key }}%
              </MedButton>
            </div>

            <!-- Placeholders -->
            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <h3 class="text-sm font-semibold">Placeholders</h3>
                <MedButton variant="secondary" size="sm" @click="addPlaceholder">
                  <ComponentIcon class="mr-1 text-sm">add</ComponentIcon>
                  Add Placeholder
                </MedButton>
              </div>

              <template v-for="(placeholder, index) in placeholders" :key="index">
                <div class="flex gap-2">
                  <div class="flex-1">
                    <ComponentTextarea v-model="placeholder.value" :label="'Placeholder: %' + placeholder.key + '%'" />
                  </div>
                  <div v-if="placeholder.isCustom" class="flex items-end pb-1">
                    <MedButton variant="destructive" size="sm" @click="removePlaceholder(index)">
                      <ComponentIcon class="text-sm">delete</ComponentIcon>
                    </MedButton>
                  </div>
                </div>
              </template>
            </div>

            <!-- Technical Terms -->
            <div class="space-y-3">
              <MedFormField orientation="horizontal">
                <MedCheckbox v-model="form.replace_technical_terms" variant="default" />
                <MedLabel clickable>Replace technical with layman terms</MedLabel>
              </MedFormField>
              <ComponentTextarea
                v-if="form.replace_technical_terms"
                v-model="technicalTermsJson"
                label="Technical Terms (key = technical term → value = replacement)"
              />
            </div>

            <!-- Structured Output Schema -->
            <div class="space-y-3">
              <MedFormField orientation="horizontal">
                <MedCheckbox v-model="form.enable_schema" variant="default" />
                <MedLabel clickable>Structured Output (Schema)</MedLabel>
              </MedFormField>
              <ComponentTextarea v-if="form.enable_schema" v-model="schemaJson" label="JSON Schema" />
            </div>

            <div v-if="supportsThinking" class="flex gap-5">
              <ComponentSelect
                v-model="form.thinking_level"
                :disabled="thinkingLevels.length === 0"
                label="Reasoning / Thinking Level"
                class="w-full"
                placeholder-text="Standard"
                :nullable="true"
              >
                <option v-for="thinkingLevel in thinkingLevels" :key="thinkingLevel" :value="thinkingLevel">
                  {{ thinkingLevel }}
                </option>
              </ComponentSelect>

              <ComponentInput
                v-if="supportsThinkingBudget"
                v-model="form.thinking_budget"
                type="number"
                label="Thinking Budget (Tokens)"
              />
            </div>

            <div class="flex gap-5">
              <ComponentInput v-model="form.max_steps" type="number" label="Max Steps" />
              <ComponentInput v-model="form.max_tokens" type="number" label="Max Tokens" />
            </div>

            <div class="flex gap-5">
              <template v-if="supportsTemperature">
                <ComponentInput v-model="form.temperature" type="number" label="Temperature" />
              </template>

              <template v-if="supportsTopP">
                <ComponentInput v-model="form.top_p" type="number" label="Top P" />
              </template>
            </div>
          </MedCardContent>
        </MedCard>

        <!-- Actions -->
        <MedCard variant="flat" class="sticky bottom-4 z-10">
          <MedCardContent class="flex items-center gap-3">
            <MedButton variant="primary" :disabled="!canGenerate" :is-loading="isLoading" @click="startQueueGeneration">
              Generate (Queue)
            </MedButton>

            <MedButton v-if="isLoading" variant="destructive" @click="abort">Cancel</MedButton>

            <div class="ml-auto flex items-center gap-3 text-sm">
              <AiStatusBadge v-if="jobStatus" :status="jobStatus" prefix="Job:" />
              <span>⏱ {{ durationDisplay }}</span>
            </div>
          </MedCardContent>
        </MedCard>

        <!-- Error -->
        <div v-if="errorMessage" class="rounded-lg border border-red-300 bg-red-50 p-4 text-red-800">
          <strong>Error:</strong>
          {{ errorMessage }}
        </div>

        <!-- Results -->
        <AiResultCard v-if="resultData" :result="resultData" />
      </div>
      <!-- /main content -->
    </div>
  </div>
</template>
